import React from 'react';
import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';

import { FormSubmitEvent } from 'ui/base/types';
import { Theme, useStyles } from 'ui/base/theme';
import { ActiveButton } from 'ui/public/button';
import { LabeledInput, TextInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';
import { PartitionedPage } from 'ui/add_pangolin/partitioned_page/partitioned_page';

import { ResultKind } from 'services/base/types';
import { BasePath, pagePathToPathString } from 'services/base/page_path';

import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import baseStyles from './add_pangolin_rename.module.css';
import AddPangolinRenameMessages from './add_pangolin_rename_messages';
import { AddPangolinRenameStore } from './add_pangolin_rename_store';

type AddPangolinRenameStyles = {
  title: string;
  instructions: string;
  image: string;
  checkImage: string;
  action: string;
};

const themedStyles: Record<Theme, AddPangolinRenameStyles> = {
  [Theme.DAYLIGHT]: {
    title: baseStyles.daylightTitle,
    instructions: baseStyles.daylightInstructions,
    image: baseStyles.image,
    checkImage: baseStyles.checkImage,
    action: baseStyles.action,
  },
  [Theme.MIDNIGHT]: {
    title: baseStyles.midnightTitle,
    instructions: baseStyles.midnightInstructions,
    image: baseStyles.image,
    checkImage: baseStyles.checkImage,
    action: baseStyles.action,
  },
};

type BaseAddPangolinRenameProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
};

export const BaseAddPangolinRename = observer(({ accounts, pangolin }: BaseAddPangolinRenameProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const [store] = React.useState(new AddPangolinRenameStore());

  const handleSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();

    const result = await store.busy.perform(() => store.handleSubmit(accounts, pangolin.id));

    if (result.kind === ResultKind.OK) {
      history.replace(pagePathToPathString({
        kind: BasePath.ADD_PANGOLIN_PROFILE,
        pangolinID: pangolin.id,
      }));
    }
  }, [pangolin, store, accounts, history]);

  const top = (
    <>
      <div className={styles.title}>
        {AddPangolinRenameMessages.title()}
      </div>
      <div className={styles.instructions}>
        {AddPangolinRenameMessages.instructions()}
      </div>
    </>
  );

  const middle = <div className={styles.image}/>;

  const bottom = (
    <form onSubmit={handleSubmit}>
      <LabeledInput label={AddPangolinRenameMessages.nickname()}>
        <TextInput store={store.nickname}/>
      </LabeledInput>
      <ErrorMessage message={store.nicknameError}/>
      <div className={styles.action}>
        <ActiveButton busy={store.busy} type='submit'>
          {AddPangolinRenameMessages.next()}
        </ActiveButton>
      </div>
    </form>
  );

  return (
    <PartitionedPage top={top} middle={middle} bottom={bottom}/>
  );
});

export const AddPangolinRename = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  return (
    <BaseAddPangolinRename
      accounts={accounts}
      pangolin={pangolin}
    />
  );
});
